import * as React from 'react';
import { Check, Flame } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ModelImage } from '@acme/db';
import { Button } from '@components/ui/button';
import Link from 'next/link';
import { PhotoPackSamplePhoto } from '@components/Theme/PhotoPackSamplePhoto';
import { PhotoPackName, PhotoPackTag } from '.';
import { Skeleton } from '@components/ui/skeleton';
interface ListItemProps {
  className?: string;
  enableSampleGallery?: boolean;
  photoCount?: number;
  showPhotoCount?: boolean;
  checked?: boolean;
  usageCount?: number;
  style?: React.CSSProperties;
  showUsageCount?: boolean;
  onCustomize?: (checked: boolean) => void;
  relationship?: 'couple' | 'friends';
  isSheetOpen?: boolean;
  setIsSheetOpen?: (open: boolean) => void;
  // Additional props
  isLoggedIn?: boolean;
  limitSamples?: number;
  disableLink?: boolean;
  skeleton?: boolean;
  photoPack?: {
    name: string;
    id: string;
    tag: {
      name: string;
      url: string;
    };
    url: string;
    sampleImages?: (Pick<ModelImage, 'id' | 'imageUrl'> & { selfieImageUrl?: string | null })[];
  };
}

export function ListItem({
  checked = false,
  usageCount,
  showUsageCount = true,
  onCustomize,
  limitSamples = 2,
  photoPack,
  disableLink = false,
  skeleton = false
}: ListItemProps) {
  // Ensure we have sample images to display
  const hasValidSamples =
    photoPack?.sampleImages?.length! > 0 && photoPack?.sampleImages?.every(sample => sample && 'imageUrl' in sample);

  if (skeleton) {
    return (
      <Card className="flex flex-row h-fit overflow-hidden p-2 bg-transparent hover:bg-foreground/10 border-none gap-3 items-center relative animate-pulse">
        {/* Image container - fixed width with two images side by side */}
        <div className="w-16 min-w-16 flex justify-start items-center rounded-md overflow-hidden gap-0.5">
          <Skeleton className="flex-1 h-12 animate-none bg-foreground/10" />
          <Skeleton className="flex-1 h-12 animate-none bg-foreground/10" />
        </div>

        <div className="flex-1 inline-flex flex-col justify-start items-start overflow-hidden gap-2">
          <Skeleton className="h-3 w-3/4 mb-2 animate-none bg-foreground/10" />
          <Skeleton className="h-3 w-1/3 mb-1 animate-none bg-foreground/10" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="flex flex-row h-fit overflow-hidden p-2 bg-transparent hover:bg-foreground/10 border-none gap-3 items-center relative">
        {/* Image container - fixed width with two images side by side */}
        <div className="w-16 min-w-16 flex justify-start items-center rounded-md overflow-hidden gap-0.5">
          {!hasValidSamples ? (
            <div className="flex-1 h-12 bg-muted/50 text-xs flex items-center justify-center text-muted-foreground">
              No samples
            </div>
          ) : (
            <>
              {photoPack?.sampleImages?.slice(0, limitSamples).map(image => (
                <div key={image.id} className="flex-1 h-12 overflow-hidden">
                  {image && image.imageUrl ? (
                    disableLink ? (
                      <div className="relative w-full h-full">
                        <PhotoPackSamplePhoto
                          imageUrl={image.imageUrl}
                          id={image.id}
                          showSelfie={false}
                          forceTallAspectRatio={true}
                        />
                      </div>
                    ) : (
                      <Link href={photoPack?.url} className="relative w-full h-full block">
                        <PhotoPackSamplePhoto
                          imageUrl={image.imageUrl}
                          id={image.id}
                          showSelfie={false}
                          forceTallAspectRatio={true}
                        />
                      </Link>
                    )
                  ) : (
                    <div className="h-full w-full bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {showUsageCount && usageCount ? (
            <Badge
              variant="default"
              className="absolute top-0 left-0 rounded-none whitespace-nowrap text-[10px] !max-w-none font-medium text-white bg-black/70 z-10 items-center justify-center"
            >
              <Flame className="h-3 w-3 mr-0.5 fill-orange-500 stroke-none text-orange-500" />
              {usageCount}
            </Badge>
          ) : null}
        </div>

        <div className="flex-1 inline-flex flex-col justify-start items-start overflow-hidden">
          <PhotoPackTag
            photoPack={photoPack!}
            disableLink={disableLink}
            className="self-stretch text-xs text-muted-foreground uppercase tracking-wider truncate"
          />
          <PhotoPackName
            photoPack={photoPack!}
            disableLink={disableLink}
            className="self-stretch text-base font-semibold truncate"
          />
        </div>

        <Button
          size="sm"
          variant="secondary"
          className="px-6 py-2 rounded-full flex-shrink-0 text-accent font-semibold"
          onClick={() => {
            onCustomize?.(!checked);
          }}
        >
          {checked ? 'Edit' : 'Get'}
        </Button>

        {checked && (
          <div className="absolute right-2 top-2">
            <div className="w-5 h-5 bg-accent text-white rounded-full shadow-sm flex items-center justify-center">
              <Check className="w-3 h-3" />
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
